// app/javascript/components/EditPersonnel.jsx
import React, { useState, useRef, useEffect } from 'react';
import { useArmyDepartments } from '../hooks/useArmyDepartments';
import { useStaticRegionDistricts } from '../hooks/useRegionDistricts';
import { useRanks } from '../hooks/useRanks';
import { t } from '../utils/i18n';
import { parseApiErrors } from '../utils/formatApiErrors';
import SignaturePad from './SignaturePad';
import WebcamCaptureModal from './WebcamCaptureModal';


// Returns true if the base64 string is a known image format.
const isImageBase64 = (b64) => {
  if (!b64 || typeof b64 !== 'string') return false;
  const c = b64.replace(/\s+/g, '');
  return c.startsWith('data:image/') || c.startsWith('iVBOR') || c.startsWith('Qk') || c.startsWith('/9j/');
};

// Build the correct data URL from a raw base64 string.
const toDataUrl = (b64) => {
  const c = b64.replace(/\s+/g, '');
  if (c.startsWith('data:')) return c;
  if (c.startsWith('iVBOR')) return `data:image/png;base64,${c}`;
  if (c.startsWith('/9j/')) return `data:image/jpeg;base64,${c}`;
  if (c.startsWith('Qk')) return `data:image/bmp;base64,${c}`;
  return `data:image/png;base64,${c}`;
};

// Renders a fingerprint image. PNG and JPEG render directly.
// Falls back to "Template enrolled" badge if no valid image data.
const FingerprintCanvas = React.memo(({ templateBase64, imageBase64 }) => {
  const [failed, setFailed] = React.useState(false);

  const boxStyle = {
    width: '120px',
    height: '140px',
    borderRadius: '4px',
    border: '1px solid #ddd',
    objectFit: 'contain',
    background: '#f5f5f5',
    imageRendering: 'crisp-edges',
    display: 'block',
  };

  const hasImage = imageBase64 && isImageBase64(imageBase64) && !failed;

  if (hasImage) {
    return (
      <img
        src={toDataUrl(imageBase64)}
        alt="Fingerprint"
        onError={() => setFailed(true)}
        style={boxStyle}
      />
    );
  }

  const hasTemplate = !!(templateBase64 || imageBase64);
  return (
    <div style={{
      ...boxStyle,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '6px',
      padding: '10px',
      textAlign: 'center',
    }}>
      <span style={{ fontSize: '28px' }}>🖐</span>
      <span style={{ fontSize: '10px', color: hasTemplate ? '#2e7d32' : '#999', fontWeight: hasTemplate ? 600 : 400 }}>
        {hasTemplate ? 'Template enrolled' : 'No data'}
      </span>
    </div>
  );
});

const sanitizeBase64 = (value) => {
  if (!value) return '';
  return value.replace(/\s+/g, '').trim();
};

const formatFingerprintImageSrc = (value) => {
  const cleaned = sanitizeBase64(value);
  if (!cleaned) return '';
  if (cleaned.startsWith('data:image/')) return cleaned;
  return `data:image/bmp;base64,${cleaned}`;
};

const EditPersonnel = ({ onBack, onNavigate, api, userId, personnelId, subView = 'military' }) => {
  const [formData, setFormData] = useState({
    fullName: '',
    gender: 'Male',
    maritalStatus: 'Single',
    educationalLevel: "High School",
    bloodType: 'O+',
    birthDate: '',
    phone: '',
    email: '',
    emergencyContact: '',
    armyType: '',
    salaryUsd: '',
    address: '',
    allowanceUsd: '',
    bankAccount: '',
    emergencyContactPhone: '',
    emergencyContactRelation: '',
    responsiblePersonName: '',
    responsiblePersonPhone: '',
    training: '',
    motherName: '',
    remarks: '',
    armyDepartment: '',
    subDepartment: '',
    rank: '',
    armyEquipment: '',
    district: '',
    region: '',
    station: '',
    status: '',
    title: '',
    placeOfBirth: '',
    previousData: '',
    organization: '',
    workPlace: '',
    activeStatus: 0,
    biometricEnrolledCount: 0
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dataLoaded, setDataLoaded] = useState(false);
  const { armyTypes, filteredDepartments, filteredSubDepartments, handleArmyTypeChange, handleDepartmentChange, toSomali } = useArmyDepartments(api, formData.armyType, formData.armyDepartment);
  const { regions, filteredDistricts, handleRegionChange } = useStaticRegionDistricts();
  const { ranks } = useRanks(api);
  const [photo, setPhoto] = useState(null);
  const [armyPhotoBase64, setArmyPhotoBase64] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');
  const [showWebcamModal, setShowWebcamModal] = useState(false);
  const [facePhoto, setFacePhoto] = useState(null);
  const [faceTemplate, setFaceTemplate] = useState(null);
  const [faceRecordId, setFaceRecordId] = useState(null);  // existing BiometricData id
  const [speedfaceFingerprint, setSpeedfaceFingerprint] = useState(null);  // fingerprint from SpeedFace device
  const [signature, setSignature] = useState('');
  const [documents, setDocuments] = useState([]);   // existing docs from DB: [{name, data, doc_type}]
  const [newDocFiles, setNewDocFiles] = useState([]); // newly selected File objects
  const [enrolledFingerprints, setEnrolledFingerprints] = useState([]);  // desktop app fingerprints

  // Re-capture state (mirrors AddPersonnel)
  const [availableSpeedfaceDevices, setAvailableSpeedfaceDevices] = useState([]);
  const [speedfaceDevice, setSpeedfaceDevice] = useState('');
  const [devicePin, setDevicePin] = useState('');
  const [faceCapturing, setFaceCapturing] = useState(false);
  const [faceStatus, setFaceStatus] = useState('');
  const [captureAttempts, setCaptureAttempts] = useState(0);
  const [newFaceData, setNewFaceData] = useState(null);  // { photo, template } from re-capture
  const [newFpData, setNewFpData] = useState(null);
  const [duplicateFace, setDuplicateFace] = useState(null);
  const [bioSaving, setBioSaving] = useState(false);
  const [bioSaveStatus, setBioSaveStatus] = useState('');
  const facePollingRef = useRef(null);
  useEffect(() => {
    if (personnelId) {
      fetchPersonnel();
    }
  }, [personnelId]);

  useEffect(() => {
    // When personnel data is loaded, ensure the hook filters are run so
    // `filteredDepartments` and `filteredSubDepartments` are populated
    // and the selects show the correct related options.
    if (dataLoaded) {
      if (formData.armyType) {
        handleArmyTypeChange(formData.armyType);
      }
      if (formData.armyDepartment) {
        // run after armyType is applied to ensure proper filtering
        setTimeout(() => handleDepartmentChange(formData.armyDepartment), 0);
      }
    }
  }, [dataLoaded]);

  useEffect(() => {
    if (dataLoaded && formData.region) {
      handleRegionChange(formData.region);
    }
  }, [dataLoaded, formData.region]);

  useEffect(() => {
    if (dataLoaded && formData.armyType) {
      handleArmyTypeChange(formData.armyType);
      if (formData.armyDepartment) {
        handleDepartmentChange(formData.armyDepartment);
      }
    }
  }, [dataLoaded]);
  const fetchPersonnel = async () => {
    setLoading(true);
    setError('');
    try {
      const targetId = (personnelId && personnelId !== 'undefined' && personnelId !== 'null')
        ? personnelId
        : (userId && userId !== 'undefined' && userId !== 'null')
          ? userId
          : null;

      if (!targetId) {
        throw new Error('No valid Personnel ID or User ID provided');
      }

      const res = await api.get(`/army_personnel/${targetId}${userId ? `?user_id=${userId}` : ''}`);
      const personnel = res?.data?.army_personnel || res?.army_personnel || res?.data || res;

      if (!personnel || res?.error || personnel.error || !personnel.id) {
        throw new Error(res?.error || personnel?.error || 'Personnel record not found');
      }

      // Debug: confirm photo and biometric_data are present
      console.log('[EditPersonnel] army_photo present:', !!personnel.army_photo, 'length:', personnel.army_photo?.length);
      console.log('[EditPersonnel] biometric_data count:', personnel.biometric_data?.length);
      console.log('[EditPersonnel] biometric types:', personnel.biometric_data?.map(b => b.biometric_type));

      const biometricData = personnel.biometric_data || [];

      const isFingerprintRecord = (value) => {
        return value === 'fingerprint' || value === 0 || value === '0';
      };

      // Separate SpeedFace fingerprints from desktop fingerprints
      const allFingerprints = biometricData.filter(bio => isFingerprintRecord(bio.biometric_type));

      // Debug: log what arrived so we can diagnose display issues
      console.log('[EditPersonnel] biometric_data from API:', biometricData.map(b => ({
        id: b.id,
        biometric_type: b.biometric_type,
        has_data: !!b.data,
        data_prefix: b.data?.substring(0, 40),
        has_raw_template: !!b.raw_template,
        raw_template_prefix: b.raw_template?.substring(0, 40)
      })));
      console.log('[EditPersonnel] fingerprint records found:', allFingerprints.length);

      const speedfaceFp = allFingerprints.find(bio =>
        bio.raw_template && bio.raw_template.includes('DEVPIN:')
      );

      const desktopFingerprints = allFingerprints
        .filter(bio => !bio.raw_template || !bio.raw_template.includes('DEVPIN:'))
        .map((bio, index) => {
          // bio.data may be a BMP/PNG image OR a raw binary template (not displayable).
          // Only pass it as `image` if it looks like an actual image format.
          const dataVal = bio.data || '';
          const looksLikeImage = isImageBase64(dataVal);
          return {
            id: bio.id,
            fingerName: `Finger ${index + 1}`,
            template: bio.raw_template || '',
            image: looksLikeImage ? dataVal : null  // null → FingerprintCanvas shows "Template enrolled"
          };
        });

      // Calculate total enrollment count
      const totalFingerprints = desktopFingerprints.length + (speedfaceFp ? 1 : 0);

      // Helper to format dates for HTML date inputs (YYYY-MM-DD)
      const formatDateForInput = (val) => {
        if (!val) return '';
        try {
          const d = new Date(val);
          if (isNaN(d.getTime())) return '';
          return d.toISOString().slice(0, 10);
        } catch (e) {
          return '';
        }
      };

      setFormData({
        fullName: personnel.full_name || '',
        gender: personnel.gender || 'Male',
        maritalStatus: personnel.marital_status || 'Single',
        educationalLevel: personnel.educational_level || 'High School',
        bloodType: personnel.blood_type || 'O+',
        birthDate: formatDateForInput(personnel.birth_date) || '',
        phone: personnel.phone || '',
        email: personnel.user?.email || '',
        emergencyContact: personnel.emergency_contact || '',
        armyType: personnel.army_type_id || '',
        salaryUsd: personnel.salary_usd || '',
        address: personnel.address || '',
        allowanceUsd: personnel.allowance_usd || '',
        bankAccount: personnel.bank_account || '',
        bankName: personnel.bank_name || '',
        emergencyContactPhone: personnel.emergency_contact_phone || '',
        emergencyContactRelation: personnel.emergency_contact_relation || '',
        responsiblePersonName: personnel.responsible_person_name || '',
        responsiblePersonPhone: personnel.responsible_person_phone || '',
        training: personnel.training || '',
        motherName: personnel.mother_name || '',
        remarks: personnel.remarks || '',
        armyDepartment: personnel.department_id || '',
        subDepartment: personnel.sub_department_id || '',
        rank: personnel.rank || '',
        armyEquipment: personnel.army_equipment || '',
        district: personnel.location?.district || '',
        region: personnel.location?.region || '',
        station: personnel.location?.station || '',
        status: personnel.status,
        previousData: personnel.prev_data || '',
        title: personnel.title || '',
        placeOfBirth: personnel.place_of_birth || '',
        organization: personnel.organization || '',
        workPlace: personnel.work_place || '',
        activeStatus: personnel.active_status || 0,
        biometricEnrolledCount: Math.max(totalFingerprints, personnel.biometric_enrolled_count || 0)
      });

      const signatureData = biometricData.find(bio =>
        bio.biometric_type === 'signature' || bio.biometric_type === 1 || bio.biometric_type === '1'
      );

      // Load signature
      const sigValue = signatureData?.data || null;
      if (sigValue && typeof sigValue === 'string' && sigValue.trim().length > 0) {
        const cleaned = sigValue.trim().replace(/\s+/g, '');
        if (cleaned.startsWith('data:')) {
          setSignature(cleaned);
        } else {
          setSignature(`data:image/png;base64,${cleaned}`);
        }
      }

      // Load face biometric from biometric_data array (ONLY from SpeedFace device)
      // Desktop resync photos use type='photo' but store in army_personnels.army_photo field, NOT biometric_data
      // SpeedFace device photos have raw_template starting with "DEVPIN:"
      const faceRecord = biometricData.find(bio =>
        (bio.biometric_type === 'photo' || bio.biometric_type === 2 || bio.biometric_type === '2') &&
        bio.raw_template && bio.raw_template.includes('DEVPIN:')
      );
      if (faceRecord) {
        setFaceRecordId(faceRecord.id || null);
        const tmpl = faceRecord.raw_template || faceRecord.data;
        if (tmpl) setFaceTemplate(tmpl.length);
        // Only set face photo if data is a real JPEG (starts with /9j/ in base64)
        const d = faceRecord.data || '';
        const isJpeg = d.startsWith('/9j/') || d.startsWith('iVBOR') || d.startsWith('data:image/jpeg') || d.startsWith('data:image/png');
        if (isJpeg) setFacePhoto(d.startsWith('data:') ? d : `data:image/jpeg;base64,${d}`);
      }

      // Army photo - load from army_photo field (stored as data: URL or raw base64)
      const rawPhoto = personnel.army_photo;
      if (rawPhoto && typeof rawPhoto === 'string' && rawPhoto.trim().length > 0) {
        const cleaned = rawPhoto.trim().replace(/\s+/g, '');
        if (cleaned.startsWith('data:')) {
          setPhotoPreview(cleaned);
        } else if (cleaned.length > 100) {
          // Raw base64 without prefix — detect format
          if (cleaned.startsWith('/9j/')) {
            setPhotoPreview(`data:image/jpeg;base64,${cleaned}`);
          } else if (cleaned.startsWith('iVBOR')) {
            setPhotoPreview(`data:image/png;base64,${cleaned}`);
          } else {
            setPhotoPreview(`data:image/jpeg;base64,${cleaned}`);
          }
        }
      }
      // Load documents stored in the personnel record
      try {
        const rawDocs = personnel.documents;
        if (rawDocs) {
          const parsed = typeof rawDocs === 'string' ? JSON.parse(rawDocs) : rawDocs;
          setDocuments(Array.isArray(parsed) ? parsed : []);
        }
      } catch (_) { }

      // Set fingerprint state after form data is initialized
      if (speedfaceFp) setSpeedfaceFingerprint(speedfaceFp.raw_template || speedfaceFp.data);
      setEnrolledFingerprints(desktopFingerprints);

      setDataLoaded(true);
    } catch (err) {
      console.error('[EditPersonnel] fetchPersonnel error:', err);
      setError(`Failed to load personnel data: ${err.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  // Load speedface devices
  useEffect(() => {
    api.get('/biometric_devices').then(result => {
      const devices = result?.data?.biometric_devices || [];
      setAvailableSpeedfaceDevices(devices);
      // Do not auto-select — user must choose the device
    }).catch(() => { });
  }, []);

  useEffect(() => () => clearInterval(facePollingRef.current), []);

  const handleFaceRecapture = async () => {
    if (!speedfaceDevice) { setFaceStatus('Please select a device first.'); return; }
    if (!devicePin) { setFaceStatus('Please enter the device PIN first.'); return; }

    clearInterval(facePollingRef.current);
    setFaceCapturing(true);
    setNewFaceData(null);
    setNewFpData(null);
    setDuplicateFace(null);
    setCaptureAttempts(0);
    setFaceStatus('Querying device for PIN ' + devicePin + '...');

    await api.get(`/speedface/capture_face?device_sn=${encodeURIComponent(speedfaceDevice)}&pin=${encodeURIComponent(devicePin)}`);

    let attempts = 0;
    facePollingRef.current = setInterval(async () => {
      attempts++;
      setCaptureAttempts(attempts);
      try {
        const result = await api.get(
          `/speedface/pending?device_sn=${encodeURIComponent(speedfaceDevice)}&pin=${encodeURIComponent(devicePin)}&personnel_id=${personnelId}`
        );
        const data = result?.data;
        if (data?.found) {
          clearInterval(facePollingRef.current);
          if (data.duplicate && data.duplicate.id !== personnelId) {
            setDuplicateFace(data.duplicate);
            setFaceStatus(`⚠️ Already registered as: ${data.duplicate.name} (ID: ${data.duplicate.id}). Blocked.`);
            setFaceCapturing(false);
            return;
          }
          setNewFaceData({ template: data.template || null, photo: data.photo || null });
          try {
            const fpRes = await api.get(`/speedface/fetch_all?device_sn=${encodeURIComponent(speedfaceDevice)}&pin=${encodeURIComponent(devicePin)}`);
            if (fpRes?.data?.fingerprint) setNewFpData({ template: fpRes.data.fingerprint });
          } catch (_) { }
          setFaceCapturing(false);
          setFaceStatus(data.photo ? '✓ New face photo + template ready. Click Save Biometric.' : '✓ New face template ready. Click Save Biometric.');
        } else if (attempts >= 24) {
          clearInterval(facePollingRef.current);
          setFaceCapturing(false);
          setFaceStatus('Timed out. Ensure PIN ' + devicePin + ' is enrolled on the device.');
        } else {
          setFaceStatus(`Waiting for device... ${attempts * 5}s / 120s (PIN: ${devicePin})`);
        }
      } catch (err) { console.error('Polling error', err); }
    }, 5000);
  };

  const handleSaveBiometric = async () => {
    if (!newFaceData && !newFpData) return;
    setBioSaving(true);
    setBioSaveStatus('');
    try {
      // Upsert face if captured, otherwise delete it from DB
      if (newFaceData?.photo || newFaceData?.template) {
        const faceDataValue = newFaceData.photo || newFaceData.template;
        const faceResult = await api.post('/biometric_data/upsert', {
          army_personnel_id: personnelId,
          biometric_type: 'photo',
          data: faceDataValue,
          raw_template: `DEVPIN:${speedfaceDevice}:${devicePin}:${newFaceData.template || faceDataValue}`
        });
        if (!faceResult.response.ok) throw new Error(faceResult.data?.errors?.join(', ') || faceResult.data?.error || 'Failed to save face');
        if (newFaceData.photo) setFacePhoto(newFaceData.photo.startsWith('data:') ? newFaceData.photo : `data:image/jpeg;base64,${newFaceData.photo}`);
        if (newFaceData.template) setFaceTemplate(newFaceData.template.length);
      } else {
        // Face was not returned by device — remove from DB
        await api.delete(`/biometric_data/destroy_by_type?army_personnel_id=${personnelId}&biometric_type=photo`);
        setFacePhoto(null);
        setFaceTemplate(null);
      }

      // Upsert fingerprint if captured from SpeedFace device
      if (newFpData?.template) {
        const fpResult = await api.post('/biometric_data/upsert', {
          army_personnel_id: personnelId,
          biometric_type: 'fingerprint',
          data: newFpData.template,
          raw_template: `DEVPIN:${speedfaceDevice}:${devicePin}:${newFpData.template}`
        });
        if (!fpResult.response.ok) throw new Error(fpResult.data?.errors?.join(', ') || fpResult.data?.error || 'Failed to save fingerprint');
        setSpeedfaceFingerprint(newFpData.template);
      } else {
        // Fingerprint was not returned by device — remove SpeedFace fingerprint from DB
        const allBiometrics = await api.get(`/biometric_data?army_personnel_id=${personnelId}`);
        const speedfaceFp = allBiometrics?.data?.biometric_data?.find(bio =>
          (bio.biometric_type === 'fingerprint' || bio.biometric_type === 0) &&
          bio.raw_template && bio.raw_template.includes('DEVPIN:')
        );
        if (speedfaceFp?.id) {
          await api.delete(`/biometric_data/${speedfaceFp.id}`);
        }
        setSpeedfaceFingerprint(null);
      }

      await api.delete(`/speedface/pending?device_sn=${encodeURIComponent(speedfaceDevice)}&pin=${encodeURIComponent(devicePin)}`);
      setNewFaceData(null);
      setNewFpData(null);
      setFaceStatus('');
      setDuplicateFace(null);
      setBioSaveStatus('✓ Biometric data updated successfully.');
    } catch (err) {
      setBioSaveStatus(`Failed to save biometric data: ${err.message}`);
    } finally {
      setBioSaving(false);
    }
  };

  const handleChangeArmyType = (e) => {
    const { name, value } = e.target;

    if (name === 'armyType') {
      handleArmyTypeChange(value);
      setFormData({ ...formData, [name]: value, armyDepartment: '', subDepartment: '' });
    } else if (name === 'armyDepartment') {
      handleDepartmentChange(value);
      setFormData({ ...formData, [name]: value, subDepartment: '' });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleChangeRegion = (e) => {
    const { name, value } = e.target;

    if (name === 'region') {
      handleRegionChange(value);
      setFormData({ ...formData, [name]: value, district: '' });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Read any newly selected document files as base64
    const readNewDocs = () => Promise.all(
      newDocFiles.map(file => new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (ev) => resolve({
          name: file.name,
          doc_type: file.type,
          data: ev.target.result.split(',')[1]
        });
        reader.readAsDataURL(file);
      }))
    );

    try {
      const newDocs = newDocFiles.length > 0 ? await readNewDocs() : [];
      const allDocs = [...documents, ...newDocs];

      const payload = {
        army_personnel: {
          full_name: formData.fullName,
          gender: formData.gender,
          marital_status: formData.maritalStatus,
          educational_level: formData.educationalLevel,
          blood_type: formData.bloodType,
          birth_date: formData.birthDate,
          phone: formData.phone,
          email: formData.email,
          emergency_contact: formData.emergencyContact,
          army_type_id: formData.armyType,
          salary_usd: formData.salaryUsd,
          address: formData.address,
          allowance_usd: formData.allowanceUsd,
          bank_account: formData.bankAccount,
          bank_name: formData.bankName,
          emergency_contact_phone: formData.emergencyContactPhone,
          emergency_contact_relation: formData.emergencyContactRelation,
          responsible_person_name: formData.responsiblePersonName,
          responsible_person_phone: formData.responsiblePersonPhone,
          training: formData.training,
          mother_name: formData.motherName,
          remarks: formData.remarks,
          department_id: formData.armyDepartment,
          sub_department_id: formData.subDepartment,
          rank: formData.rank,
          army_equipment: formData.armyEquipment,
          district: formData.district,
          region: formData.region,
          station: formData.station,
          status: formData.status,
          prev_data: formData.previousData,
          title: formData.title,
          place_of_birth: formData.placeOfBirth,
          organization: formData.organization,
          work_place: formData.workPlace,
          active_status: formData.activeStatus,
          biometric_enrolled_count: formData.biometricEnrolledCount,
          ...(signature && { biometric_signature: signature }),
          ...(armyPhotoBase64 && { army_photo: armyPhotoBase64 }),
          ...(allDocs.length > 0 && { documents: allDocs })
        }
      };

      const { response, data } = await api.put(`/army_personnel/${personnelId}?user_id=${userId}`, payload);

      if (response.ok) {
        onNavigate(`personnel-${subView}`);
      } else {
        const { messages } = parseApiErrors(data);
        setError(messages.join('\n'));
      }
    } catch (err) {
      console.error('EditPersonnel handleSubmit error:', err);
      setError(`Update failed: ${err.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhoto(file);
      const reader = new FileReader();
      reader.onload = (ev) => {
        setPhotoPreview(ev.target.result);
        setArmyPhotoBase64(ev.target.result.split(',')[1]);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleWebcamPhotoCapture = (dataUrl) => {
    setPhotoPreview(dataUrl);
    setArmyPhotoBase64(dataUrl.split(',')[1] || dataUrl);
    setPhoto({ name: `webcam_photo_${Date.now()}.jpg`, type: 'image/jpeg' });
    setShowWebcamModal(false);
  };

  const handleClearPhoto = (e) => {
    e.stopPropagation();
    setPhoto(null);
    setPhotoPreview('');
    setArmyPhotoBase64('');
    const input = document.getElementById('photo-upload');
    if (input) input.value = '';
  };
  const handleDocumentUpload = (e) => {
    setNewDocFiles([...e.target.files]);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const removeFingerprint = (idx) => {
    setEnrolledFingerprints(enrolledFingerprints.filter((_, i) => i !== idx));
  };

  if (loading) {
    return (
      <div className="mainContent mainContentRg">
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <p>Loading personnel data...</p>
        </div>
      </div>
    );
  }

  if (error && !dataLoaded) {
    return (
      <div className="mainContent mainContentRg">
        <div style={{ textAlign: 'center', padding: '2rem', color: 'red' }}>
          <p>{error}</p>
          <button onClick={() => onNavigate(`personnel-${subView}`)}>Back to Personnel</button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="mainContent mainContentRg">
        <div className="mainContentHD mb_48">
          <h1 className="txt24 fwSemiBold colorBlack mb_8">
            {t('editPersonnel') || 'Edit Personnel'}
          </h1>
          <p data-i18n="recordBasicInfo">
            {t('recordBasicInfo') || 'Record basic info, contacts, photo, biometric and signature'}
          </p>
        </div>

        <div className="mainContentCT">
          {error && (
            <div role="alert" style={{
              background: '#fff5f5',
              border: '1px solid #f5c6cb',
              borderLeft: '4px solid #dc3545',
              borderRadius: '6px',
              padding: '14px 16px',
              marginBottom: '20px',
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                <span style={{ fontSize: '18px', lineHeight: 1, marginTop: '1px' }}>⚠️</span>
                <div style={{ flex: 1 }}>
                  <strong style={{ display: 'block', color: '#721c24', fontSize: '14px', marginBottom: '6px' }}>
                    Update failed — please fix the following:
                  </strong>
                  <ul style={{ margin: 0, paddingLeft: '18px', listStyle: 'disc' }}>
                    {error.split('\n').filter(Boolean).map((msg, i) => (
                      <li key={i} style={{ color: '#721c24', fontSize: '13px', marginBottom: '2px' }}>{msg}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}
          <div className="mainContentRgLft">
            <div className="upldSec">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                <h2 className="txt20 fwMedium colorBlack m-0" data-i18n="armyPhoto">
                  {t('armyPhoto') || 'Army Photo'}
                </h2>
                {photoPreview && (
                  <span style={{ fontSize: '11px', background: '#e0f2fe', color: '#0369a1', padding: '2px 8px', borderRadius: '12px', fontWeight: 600 }}>
                    ✓ Ready for ID Card
                  </span>
                )}
              </div>

              <div
                className={`upload-wrapper ${photoPreview ? 'hasImg' : ''}`}
                style={{ cursor: 'pointer', position: 'relative' }}
                onClick={() => {
                  if (!photoPreview) {
                    setShowWebcamModal(true);
                  }
                }}
              >
                {!photoPreview ? (
                  <label className="upload-label" style={{ cursor: 'pointer' }}>
                    <img src="/images/cam.png" alt="" />
                    <span id="photoStatus" data-i18n="noPhotoSelected" style={{ fontSize: '13px', textAlign: 'center', color: '#64748b' }}>
                      {photo ? photo.name : (t('clickToCaptureOrUpload', 'Capture with Webcam or Upload'))}
                    </span>
                  </label>
                ) : (
                  <>
                    <img id="preview" className="preview" src={photoPreview} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    <button
                      type="button"
                      className="army-photo-corner-remove"
                      onClick={handleClearPhoto}
                      title="Remove photo"
                    >
                      ✕
                    </button>
                  </>
                )}

                <input
                  type="file"
                  id="photo-upload"
                  accept="image/*"
                  onChange={handlePhotoUpload}
                  style={{ display: 'none' }}
                />
              </div>

              <div className="army-photo-actions-wrap">
                <button
                  type="button"
                  className="btn army-photo-webcam-btn"
                  onClick={() => setShowWebcamModal(true)}
                  title="Capture live photo with webcam"
                >
                  📷 {photoPreview ? (t('retakeWebcam', 'Retake')) : (t('takePhotoWebcam', 'Use Webcam'))}
                </button>

                <button
                  type="button"
                  className="btn inv army-photo-upload-btn"
                  onClick={() => document.getElementById('photo-upload').click()}
                  title="Upload from computer"
                >
                  📁 {photoPreview ? (t('changeFile', 'Change')) : (t('upload', 'Upload'))}
                </button>
              </div>
            </div>

            {/* Face Biometric Section */}
            <div className="upldSec">
              <h2 className="txt20 fwMedium colorBlack mb_10">Face Biometric</h2>

              {/* Face photo — always shown, placeholder if missing */}
              <div className="upload-wrapper" style={{ height: '180px', cursor: 'default', overflow: 'hidden', marginBottom: '10px' }}>
                {newFaceData?.photo ? (
                  <img
                    src={newFaceData.photo.startsWith('data:') ? newFaceData.photo : `data:image/jpeg;base64,${newFaceData.photo}`}
                    alt="New Face"
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                ) : facePhoto ? (
                  <img src={facePhoto} alt="Face" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <label className="upload-label">
                    <img src="/images/cam.png" alt="" />
                    <span style={{ fontSize: '13px', color: '#aaa' }}>No face photo from device</span>
                  </label>
                )}
              </div>

              {/* Current status table - only show if face data exists */}
              {(facePhoto || faceTemplate || speedfaceFingerprint) && (
                <div style={{ border: '1px solid #e0e0e0', borderRadius: '8px', overflow: 'hidden', marginBottom: '10px' }}>
                  {[
                    { label: 'Face Photo', ok: !!facePhoto },
                    { label: 'Face Template', ok: !!faceTemplate, detail: faceTemplate ? `${faceTemplate} bytes` : null },
                    { label: 'Fingerprint', ok: !!speedfaceFingerprint, detail: speedfaceFingerprint ? 'From device' : null }
                  ].map(({ label, ok, detail }) => (
                    <div key={label} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 12px', borderBottom: '1px solid #f0f0f0', background: ok ? '#f6fff6' : '#fff8f8' }}>
                      <span style={{ fontSize: '12px', color: '#444' }}>
                        {label}{detail && <span style={{ color: '#999', marginLeft: '6px' }}>({detail})</span>}
                      </span>
                      <span style={{ fontSize: '13px', fontWeight: 700, color: ok ? '#2e7d32' : '#c62828' }}>{ok ? '✓' : '✗'}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Re-capture panel */}
              <div style={{ background: '#f9f9f9', borderRadius: '6px', padding: '10px 12px', border: '1px solid #e8e8e8' }}>
                <p style={{ fontSize: '12px', fontWeight: 600, color: '#555', marginBottom: '8px' }}>Re-capture from Device</p>

                <div className="formInpt mb_8">
                  <label style={{ fontSize: '12px' }}>Device</label>
                  {availableSpeedfaceDevices.length > 0 ? (
                    <select value={speedfaceDevice} onChange={e => setSpeedfaceDevice(e.target.value)}
                      style={{ height: '34px', fontSize: '12px', width: '100%', padding: '0 8px', border: '1px solid #ccc', borderRadius: '6px' }}>
                      <option value="">-- Select Device --</option>
                      {availableSpeedfaceDevices.map(d => (
                        <option key={d.id} value={d.device_id}>{d.device_id} — {d.model}</option>
                      ))}
                    </select>
                  ) : (
                    <input type="text" value={speedfaceDevice} onChange={e => setSpeedfaceDevice(e.target.value)}
                      placeholder="e.g. SYZ8243601042" style={{ height: '34px', fontSize: '12px' }} />
                  )}
                </div>

                <div className="formInpt mb_8">
                  <label style={{ fontSize: '12px' }}>Device PIN</label>
                  <input type="text" value={devicePin} onChange={e => setDevicePin(e.target.value)}
                    placeholder="e.g. 3" style={{ height: '34px', fontSize: '12px' }} />
                </div>

                {/* New capture result table */}
                {newFaceData && (() => {
                  const items = [
                    { label: 'Face Photo', ok: !!newFaceData.photo },
                    { label: 'Face Template', ok: !!newFaceData.template, detail: newFaceData.template ? `${newFaceData.template.length} bytes` : null },
                    { label: 'Fingerprint', ok: !!newFpData },
                  ];
                  return (
                    <div style={{ border: '1px solid #e0e0e0', borderRadius: '6px', overflow: 'hidden', marginBottom: '8px' }}>
                      {items.map(({ label, ok, detail }) => (
                        <div key={label} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '5px 10px', borderBottom: '1px solid #f0f0f0', background: ok ? '#f6fff6' : '#fff8f8' }}>
                          <span style={{ fontSize: '11px', color: '#444' }}>{label}{detail && <span style={{ color: '#999', marginLeft: '5px' }}>({detail})</span>}</span>
                          <span style={{ fontSize: '12px', fontWeight: 700, color: ok ? '#2e7d32' : '#c62828' }}>{ok ? '✓' : '✗'}</span>
                        </div>
                      ))}
                    </div>
                  );
                })()}

                {duplicateFace && (
                  <div style={{ background: '#fff3e0', border: '1px solid #ff9800', borderRadius: '6px', padding: '8px 10px', marginBottom: '8px', fontSize: '11px' }}>
                    <strong style={{ color: '#e65100' }}>⚠️ Duplicate:</strong> Already registered as <strong>{duplicateFace.name}</strong> (ID: {duplicateFace.id})
                  </div>
                )}

                {faceStatus && (
                  <p style={{ fontSize: '11px', color: newFaceData ? '#2e7d32' : duplicateFace ? '#e65100' : '#555', marginBottom: '8px' }}>
                    {faceCapturing && <span style={{ marginRight: '4px' }}>⏳</span>}{faceStatus}
                    {faceCapturing && captureAttempts > 0 && <span style={{ color: '#aaa', marginLeft: '4px' }}>({captureAttempts * 5}s)</span>}
                  </p>
                )}

                {bioSaveStatus && (
                  <p style={{ fontSize: '11px', color: bioSaveStatus.startsWith('✓') ? '#2e7d32' : '#c62828', marginBottom: '8px' }}>{bioSaveStatus}</p>
                )}

                <div style={{ display: 'flex', gap: '6px' }}>
                  <button type="button" className="btn inv" style={{ flex: 1, fontSize: '12px', minHeight: '32px' }}
                    onClick={handleFaceRecapture} disabled={faceCapturing || bioSaving}>
                    {faceCapturing ? `Waiting... (${captureAttempts * 5}s)` : '📷 Re-capture'}
                  </button>
                  {(newFaceData || newFpData) && (
                    <button type="button" className="btn primary" style={{ flex: 1, fontSize: '12px', minHeight: '32px' }}
                      onClick={handleSaveBiometric} disabled={bioSaving}>
                      {bioSaving ? 'Saving...' : '💾 Save Biometric'}
                    </button>
                  )}
                  {(newFaceData || newFpData) && (
                    <button type="button" className="btn inv" style={{ fontSize: '12px', minHeight: '32px', padding: '0 10px' }}
                      onClick={() => { setNewFaceData(null); setNewFpData(null); setFaceStatus(''); setDuplicateFace(null); }}>
                      ✕
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className="upldSec">
              <SignaturePad
                value={signature}
                onChange={(sigData) => setSignature(sigData)}
              />
            </div>



            {/* Documents Section */}
            <div className="upldSec">
              <h2 className="txt20 fwMedium colorBlack mb_10" data-i18n="documentsUpload">
                {t('documentsUpload') || 'Documents'}
              </h2>

              {/* Existing documents */}
              {documents.length > 0 && (
                <div style={{ marginBottom: '10px' }}>
                  {documents.map((doc, idx) => (
                    <div key={idx} style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '7px 10px', background: '#f9f9f9',
                      border: '1px solid #e0e0e0', borderRadius: '6px', marginBottom: '6px'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', overflow: 'hidden' }}>
                        <span style={{ fontSize: '18px' }}>
                          {/pdf/i.test(doc.doc_type || doc.name) ? '📄' :
                            /image|jpg|jpeg|png/i.test(doc.doc_type || doc.name) ? '🖼️' : '📎'}
                        </span>
                        <span style={{ fontSize: '12px', color: '#444', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {doc.name}
                        </span>
                      </div>
                      <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                        {doc.data && (
                          <a
                            href={`data:${doc.doc_type || 'application/octet-stream'};base64,${doc.data}`}
                            download={doc.name}
                            style={{ fontSize: '11px', color: '#1565c0', textDecoration: 'none' }}
                          >
                            ⬇ Download
                          </a>
                        )}
                        <button
                          type="button"
                          onClick={() => setDocuments(documents.filter((_, i) => i !== idx))}
                          style={{ background: 'none', border: 'none', color: '#c62828', cursor: 'pointer', fontSize: '13px', padding: '0 4px' }}
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* New files selected */}
              {newDocFiles.length > 0 && (
                <div style={{ marginBottom: '8px', fontSize: '12px', color: '#555' }}>
                  {newDocFiles.length} new file(s) selected — will be added on save
                </div>
              )}

              <input
                type="file"
                id="documents"
                multiple
                accept=".pdf,.doc,.docx,.jpg,.png"
                className="customFileInpt"
                onChange={handleDocumentUpload}
              />
            </div>
          </div>
          <form id="personnelForm" className="mainContentRgRgt" onSubmit={handleSubmit}>
            <div className="formBlockMain mb_18 formBlock">
              <div className="mb_18 formBlock">
                <h2 className="txt20 fwMedium colorBlack" data-i18n="personelDetails">
                  {t('personelDetails') || 'Personal Details'}
                </h2>
                <ul className="dFlex pt_8 pb_8 formFieldsWrap">
                  <li className="w_1_3">
                    <div className="formInpt">
                      <label className="mb_10" htmlFor="fullName" data-i18n="fullName">
                        {t('fullName') || 'Full Name'}
                      </label>
                      <input
                        id="fullName"
                        name="fullName"
                        type="text"
                        placeholder="John Parker"
                        value={formData.fullName}
                        onChange={handleChange}
                        required
                      />
                    </div>
                  </li>
                  <li className="w_1_3">
                    <div className="formInpt">
                      <label className="mb_10" htmlFor="motherName" data-i18n="motherName">
                        {t('motherName') || "Mother's Name"}
                      </label>
                      <input
                        id="motherName"
                        name="motherName"
                        type="text"
                        placeholder="Mother's Name"
                        value={formData.motherName}
                        onChange={handleChange}
                      />
                    </div>
                  </li>
                  <li className="w_1_3-2">
                    <div className="formInpt">
                      <label className="mb_10" htmlFor="gender" data-i18n="gender">
                        {t('gender') || 'Gender'}
                      </label>
                      <select
                        id="gender"
                        name="gender"
                        className="customSelect"
                        value={formData.gender}
                        onChange={handleChange}
                      >
                        <option value="" data-i18n="SelectGender">Select Gender</option>
                        <option value="Male" data-i18n="male">{t('male') || 'Male'}</option>
                        <option value="Female" data-i18n="female">{t('female') || 'Female'}</option>
                      </select>
                    </div>
                  </li>
                  <li className="w_1_3-2">
                    <div className="formInpt hasDatePicker">
                      <label className="mb_10" htmlFor="birthDate" data-i18n="birthDate">
                        {t('birthDate') || 'Birth Date'}
                      </label>
                      <input
                        type="date"
                        id="birthDate"
                        name="birthDate"
                        className="customDatepicker"
                        value={formData.birthDate}
                        onChange={handleChange}
                      />
                    </div>
                  </li>
                  <li className="w_1_3">
                    <div className="formInpt">
                      <label className="mb_10" htmlFor="placeOfBirth" data-i18n="placeOfBirth">
                        {t('placeOfBirth') || "Place of Birth"}
                      </label>
                      <input
                        id="placeOfBirth"
                        name="placeOfBirth"
                        type="text"
                        placeholder="Place of Birth"
                        value={formData.placeOfBirth}
                        onChange={handleChange}
                      />
                    </div>
                  </li>
                  <li className="w_1_3">
                    <div className="formInpt">
                      <label className="mb_10" htmlFor="phone" data-i18n="phone">
                        {t('phone') || 'Phone'}
                      </label>
                      <input
                        id="phone"
                        name="phone"
                        type="tel"
                        placeholder="456-xxxx-xxxx"
                        value={formData.phone}
                        onChange={handleChange}
                      />
                    </div>
                  </li>
                  <li className="w_1_3">
                    <div className="formInpt">
                      <label className="mb_10" htmlFor="status" data-i18n="status">
                        {t('status') || 'Status'}
                      </label>
                      <select
                        id="status"
                        name="status"
                        className="customSelect"
                        value={formData.status}
                        onChange={handleChange}
                      >
                        <option value="">Select Status</option>
                        <option value="heggan">Heggan</option>
                        <option value="fakad">Fakad</option>
                        <option value="dhaawac">Dhaawac</option>
                        <option value="hawlgab">Hawlgab</option>
                        <option value="naafo">Naafo</option>
                      </select>
                    </div>
                  </li>
                  <li className="w_1_3">
                    <div className="formInpt">
                      <label className="mb_10" htmlFor="educationalLevel" data-i18n="educationalLevel">
                        {t('educationalLevel') || 'Educational Level'}
                      </label>
                      <select
                        id="educationalLevel"
                        name="educationalLevel"
                        className="customSelect"
                        value={formData.educationalLevel}
                        onChange={handleChange}
                      >
                        <option value="" data-i18n="SelectEducationalLevel">Select Educational Level</option>
                        <option value="N/A">N/A</option>
                        <option value="Farbarasho">Farbarasho</option>
                        <option value="Dugsi Hoose">Dugsi Hoose</option>
                        <option value="DugsiDhexe">DugsiDhexe</option>
                        <option value="Dugsisare">Dugsisare</option>
                        <option value="machad">machad</option>
                        <option value="Jaamacad">Jaamacad</option>
                      </select>
                    </div>
                  </li>
                  <li className="w_1_3-2">
                    <div className="formInpt">
                      <label className="mb_10" htmlFor="maritalStatus" data-i18n="maritalStatus">
                        {t('maritalStatus') || 'Marital Status'}
                      </label>
                      <select
                        id="maritalStatus"
                        name="maritalStatus"
                        className="customSelect"
                        value={formData.maritalStatus}
                        onChange={handleChange}
                      >
                        <option value="" data-i18n="SelectMaritalStatus">Select Marital Status</option>
                        <option value="Single" data-i18n="single">{t('single') || 'Single'}</option>
                        <option value="Married" data-i18n="married">{t('married') || 'Married'}</option>
                      </select>
                    </div>
                  </li>
                  <li className="w_1_3-2">
                    <div className="formInpt">
                      <label className="mb_10" htmlFor="bloodType" data-i18n="bloodType">
                        {t('bloodType') || 'Blood Type'}
                      </label>
                      <select
                        id="bloodType"
                        name="bloodType"
                        className="customSelect"
                        value={formData.bloodType}
                        onChange={handleChange}
                      >
                        <option value="" data-i18n="SelectBloodType">Select Blood Type</option>
                        <option value="N/A">N/A</option>
                        <option value="O+">O+</option>
                        <option value="O-">O-</option>
                        <option value="A+">A+</option>
                        <option value="A-">A-</option>
                        <option value="B+">B+</option>
                        <option value="B-">B-</option>
                        <option value="AB+">AB+</option>
                        <option value="AB-">AB-</option>
                      </select>
                    </div>
                  </li>
                  <li className="w_1_3">
                    <div className="formInpt">
                      <label className="mb_10" htmlFor="email" data-i18n="email">
                        {t('email') || 'Email'}
                      </label>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        placeholder="name@domain"
                        value={formData.email}
                        onChange={handleChange}
                      />
                    </div>
                  </li>
                  <li className="w_1_3">
                    <div className="formInpt">
                      <label className="mb_10" htmlFor="armyType" data-i18n="armyType">
                        {t('armyType') || 'Army Type'}
                      </label>
                      <select
                        id="armyType"
                        name="armyType"
                        className="customSelect"
                        value={formData.armyType}
                        onChange={handleChangeArmyType}
                      >
                        <option value="">Select Army Type</option>
                        {armyTypes.map(type => (
                          <option key={type.id} value={type.id}>{type.name}</option>
                        ))}
                      </select>
                    </div>
                  </li>
                  <li className="w_1_3">
                    <div className="formInpt">
                      <label className="mb_10" htmlFor="armyDepartment" data-i18n="armyDepartment">
                        {t('armyDepartment') || 'Army Department'}
                      </label>
                      <select
                        id="armyDepartment"
                        name="armyDepartment"
                        className="customSelect"
                        value={formData.armyDepartment}
                        onChange={handleChangeArmyType}
                        disabled={!formData.armyType}
                      >
                        <option value="">Select Department</option>
                        {filteredDepartments.map(dept => (
                          <option key={dept.id} value={dept.id}>{dept.somali_name || dept.name}</option>
                        ))}
                      </select>
                    </div>
                  </li>
                  <li className="w_1_3">
                    <div className="formInpt">
                      <label className="mb_10" htmlFor="subDepartment" data-i18n="subDepartment">
                        {t('subDepartment') || 'Sub Department'}
                      </label>
                      <select
                        id="subDepartment"
                        name="subDepartment"
                        className="customSelect"
                        value={formData.subDepartment}
                        onChange={handleChangeArmyType}
                        disabled={!formData.armyDepartment}
                      >
                        <option value="">Select Sub Department</option>
                        {filteredSubDepartments.map(subDept => (
                          <option key={subDept.id} value={subDept.id}>{subDept.somali_name || subDept.name}</option>
                        ))}
                      </select>
                    </div>
                  </li>
                  <li className="w_1_3">
                    <div className="formInpt">
                      <label className="mb_10" htmlFor="organization" data-i18n="organization">
                        {t('organization') || 'Organization'}
                      </label>
                      <input name="organization" type="text" value={formData.organization} onChange={handleChange} />
                    </div>
                  </li>
                  <li className="w_1_3">
                    <div className="formInpt">
                      <label className="mb_10" htmlFor="rank" data-i18n="rank">
                        {t('armyRank') || 'Army Rank'}
                      </label>
                      <select
                        id="rank"
                        name="rank"
                        className="customSelect"
                        value={formData.rank}
                        onChange={handleChange}
                      >
                        <option value="" data-i18n="SelectArmyRank">{t('SelectArmyRank', 'Select Army Rank')}</option>
                        {ranks.map(r => {
                          const val = r.name || r;
                          const label = r.somali_name ? `${r.name} (${r.somali_name})` : (r.name || r);
                          return (
                            <option key={r.id || val} value={val}>
                              {label}
                            </option>
                          );
                        })}
                        {formData.rank && !ranks.some(r => (r.name || r) === formData.rank) && (
                          <option value={formData.rank}>{formData.rank}</option>
                        )}
                      </select>
                    </div>
                  </li>
                  <li className="w_1_3">
                    <div className="formInpt">
                      <label className="mb_10" htmlFor="title" data-i18n="title">
                        {t('title') || 'Title'}
                      </label>
                      <input name="title" type="text" value={formData.title} onChange={handleChange} />
                    </div>
                  </li>
                  <li className="w_1_3">
                    <div className="formInpt">
                      <label className="mb_10" htmlFor="bankName">
                        Bank Name
                      </label>
                      <select
                        id="bankName"
                        name="bankName"
                        className="customSelect"
                        value={formData.bankName}
                        onChange={handleChange}
                      >
                        <option value="">-- Select Bank Name --</option>
                        <option value="PUNTLAND STATE BANK">PUNTLAND STATE BANK</option>
                        <option value="SALAAM BANK">SALAAM BANK</option>
                        <option value="DAHABSHIIL BANK">DAHABSHIIL BANK</option>
                        <option value="AMAL BANK">AMAL BANK</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                  </li>
                  <li className="w_1_3">
                    <div className="formInpt">
                      <label className="mb_10" htmlFor="bankAccount">
                        Bank Account Number
                      </label>
                      <input
                        id="bankAccount"
                        name="bankAccount"
                        type="text"
                        placeholder="e.g. 1009876543"
                        value={formData.bankAccount}
                        onChange={handleChange}
                      />
                    </div>
                  </li>
                  <li className="w_1_3">
                    <div className="formInpt">
                      <label className="mb_10" htmlFor="salaryUsd" data-i18n="salary">
                        {t('salary') || 'Salary (USD)'}
                      </label>
                      <input
                        id="salaryUsd"
                        name="salaryUsd"
                        type="text"
                        placeholder="Managed centrally via Payroll"
                        value={formData.salaryUsd}
                        readOnly={true}
                        disabled={true}
                        style={{ backgroundColor: '#f8f9fa', cursor: 'not-allowed' }}
                      />
                    </div>
                  </li>
                  <li className="w_1_3">
                    <div className="formInpt">
                      <label className="mb_10" htmlFor="allowanceUsd" data-i18n="allowance">
                        {t('allowance') || 'Allowance (USD)'}
                      </label>
                      <input
                        id="allowanceUsd"
                        name="allowanceUsd"
                        type="text"
                        placeholder="Managed centrally via Payroll"
                        value={formData.allowanceUsd}
                        readOnly={true}
                        disabled={true}
                        style={{ backgroundColor: '#f8f9fa', cursor: 'not-allowed' }}
                      />
                    </div>
                  </li>
                  <li className="w_1_3">
                    <div className="formInpt">
                      <label className="mb_10" htmlFor="address" data-i18n="address">
                        {t('address') || 'Address'}
                      </label>
                      <input
                        id="address"
                        name="address"
                        type="text"
                        placeholder="City, District"
                        value={formData.address}
                        onChange={handleChange}
                      />
                    </div>
                  </li>
                  <li className="w_1_3">
                    <div className="formInpt">
                      <label className="mb_10" htmlFor="training" data-i18n="training">
                        {t('training') || 'Training'}
                      </label>
                      <input
                        id="training"
                        name="training"
                        type="text"
                        placeholder="Training Details"
                        value={formData.training}
                        onChange={handleChange}
                      />
                    </div>
                  </li>
                  <li className="w_1_3">
                    <div className="formInpt">
                      <label className="mb_10" htmlFor="armyEquipment" data-i18n="armyEquipment">
                        {t('armyEquipment') || 'Army Equipment'}
                      </label>
                      <input
                        id="armyEquipment"
                        name="armyEquipment"
                        type="text"
                        placeholder="Army Equipment"
                        value={formData.armyEquipment}
                        onChange={handleChange}
                      />
                    </div>
                  </li>
                  <li className="w_1_3">
                    <div className="formInpt">
                      <label className="mb_10" htmlFor="remarks" data-i18n="remarks">
                        {t('remarks') || 'Remarks'}
                      </label>
                      <input
                        id="remarks"
                        name="remarks"
                        type="text"
                        placeholder="Remarks"
                        value={formData.remarks}
                        onChange={handleChange}
                      />
                    </div>
                  </li>
                  <li className="w_1_3">
                    <div className="formInpt">
                      <label className="mb_10" htmlFor="previousData" data-i18n="previousData">
                        {t('previousData') || 'Previous Data'}
                      </label>
                      <select
                        id="previousData"
                        name="previousData"
                        className="customSelect"
                        value={formData.previousData}
                        onChange={handleChange}
                      >
                        <option value="" data-i18n="SelectPreviousData">Select Previous Data</option>
                        <option value="Haa">Haa</option>
                        <option value="Maya">Maya</option>
                      </select>
                    </div>
                  </li>
                  <li className="w_1_3">
                    <div className="formInpt">
                      <label className="mb_10" htmlFor="workPlace" data-i18n="workPlace">
                        {t('workPlace') || 'Work Place'}
                      </label>
                      <input
                        id="workPlace"
                        name="workPlace"
                        type="text"
                        placeholder="Work Place"
                        value={formData.workPlace}
                        onChange={handleChange}
                      />
                    </div>
                  </li>

                  <li className="w_1_3">
                    <div className="formInpt">
                      <label className="mb_10" htmlFor="activeStatus" data-i18n="activeStatus">
                        {t('activeStatus') || 'Active Status'}
                      </label>
                      <select
                        id="activeStatus"
                        name="activeStatus"
                        className="customSelect"
                        value={formData.activeStatus}
                        onChange={handleChange}
                      >
                        <option value="0">Pending</option>
                        <option value="1">Active</option>
                        <option value="2">Rejected</option>
                      </select>
                    </div>
                  </li>

                </ul>
              </div>
            </div>

            <div className="mb_18 formBlock">
              <h2 className="txt20 fwMedium colorBlack" data-i18n="additionalInfo">
                {t('additionalInfo') || 'Additional Information'}
              </h2>
              <ul className="dFlex pt_8 pb_8 formFieldsWrap">
                <li className="w_1_3">
                  <div className="formInpt">
                    <label className="mb_10" htmlFor="emergencyContact" data-i18n="emergencyContact">
                      {t('emergencyContact') || 'Emergency Name'}
                    </label>
                    <input
                      id="emergencyContact"
                      name="emergencyContact"
                      type="text"
                      placeholder="Name"
                      value={formData.emergencyContact}
                      onChange={handleChange}
                    />
                  </div>
                </li>
                <li className="w_1_3">
                  <div className="formInpt">
                    <label className="mb_10" htmlFor="emergencyContactPhone" data-i18n="phone">
                      {t('Contact') || 'Contact'}
                    </label>
                    <input
                      id="emergencyContactPhone"
                      name="emergencyContactPhone"
                      type="tel"
                      placeholder="456-xxxx-xxxx"
                      value={formData.emergencyContactPhone}
                      onChange={handleChange}
                    />
                  </div>
                </li>
                <li className="w_1_3">
                  <div className="formInpt">
                    <label className="mb_10" htmlFor="emergencyContactRelation" data-i18n="relationship">
                      {t('relationship') || 'Relation'}
                    </label>
                    <input
                      id="emergencyContactRelation"
                      name="emergencyContactRelation"
                      type="text"
                      placeholder="Relation"
                      value={formData.emergencyContactRelation}
                      onChange={handleChange}
                    />
                  </div>
                </li>

                <li className="w_1_3">
                  <div className="formInpt">
                    <label className="mb_10" htmlFor="responsiblePersonName" data-i18n="fullName">
                      {t('responsiblePerson') || 'Responsible Person'}
                    </label>
                    <input
                      id="responsiblePersonName"
                      name="responsiblePersonName"
                      type="text"
                      placeholder="Name"
                      value={formData.responsiblePersonName}
                      onChange={handleChange}
                    />
                  </div>
                </li>
                <li className="w_1_3">
                  <div className="formInpt">
                    <label className="mb_10" htmlFor="responsiblePersonPhone" data-i18n="contact">
                      {t('contact') || 'Contact'}
                    </label>
                    <input
                      id="responsiblePersonPhone"
                      name="responsiblePersonPhone"
                      type="tel"
                      placeholder="456-xxxx-xxxx"
                      value={formData.responsiblePersonPhone}
                      onChange={handleChange}
                    />
                  </div>
                </li>

              </ul>
            </div>

            <div className="mb_18 formBlock">
              <h2 className="txt20 fwMedium colorBlack" data-i18n="registeredLocation">
                {t('registeredLocation') || 'Registered Location'}
              </h2>
              <ul className="dFlex pt_8 pb_8 formFieldsWrap">
                <li className="w_1_3">
                  <div className="formInpt">
                    <label className="mb_10" htmlFor="region" data-i18n="region">
                      {t('region') || 'Region'}
                    </label>
                    <select name="region" value={formData.region} onChange={handleChangeRegion} required>
                      <option value="">Select Region</option>
                      {regions.map(region => (
                        <option key={region} value={region}>{region}</option>
                      ))}
                    </select>
                  </div>
                </li>
                <li className="w_1_3">
                  <div className="formInpt">
                    <label className="mb_10" htmlFor="district" data-i18n="district">
                      {t('district') || 'District'}
                    </label>
                    <select name="district" value={formData.district} onChange={handleChangeRegion} disabled={!formData.region}>
                      <option value="">Select District</option>
                      {filteredDistricts.map(district => (
                        <option key={district} value={district}>{district}</option>
                      ))}
                    </select>
                  </div>
                </li>
                <li className="w_1_3">
                  <div className="formInpt">
                    <label className="mb_10" htmlFor="station" data-i18n="station">
                      {t('station') || 'Station'}
                    </label>
                    <input
                      id="station"
                      name="station"
                      type="text"
                      placeholder="Station Name"
                      value={formData.station}
                      onChange={handleChange}
                    />
                  </div>
                </li>
                <li className="w_1_3">
                  <div className="formInpt">
                    <label className="mb_10" htmlFor="enrollmentCount" data-i18n="biometricEnrollment">
                      {t('biometricEnrollment') || 'Biometric Enrollment'}
                    </label>
                    <input
                      id="enrollmentCount"
                      name="biometricEnrolledCount"
                      type="text"
                      placeholder="Enrollment Count"
                      value={formData.biometricEnrolledCount}
                      onChange={handleChange}
                    />
                  </div>
                </li>
              </ul>
            </div>

            {/* Fingerprint Records Display - Always show if enrolled count > 0 or records exist */}
            {(enrolledFingerprints.length > 0 || formData.biometricEnrolledCount > 0) && (
              <div style={{ margin: '15px 0' }}>
                <h6 style={{ marginBottom: '10px', fontWeight: 'bold' }}>
                  Enrolled Fingerprints
                  {enrolledFingerprints.length === 0 && formData.biometricEnrolledCount > 0 && (
                    <span style={{ fontSize: '12px', fontWeight: 400, color: '#888', marginLeft: '8px' }}>
                      ({formData.biometricEnrolledCount} enrolled on device — re-sync to view)
                    </span>
                  )}
                </h6>
                {enrolledFingerprints.length > 0 ? (
                  enrolledFingerprints.map((fp, idx) => (
                    <div key={fp.id || idx} style={{ padding: '8px', background: '#e3f2fd', margin: '4px 0', borderRadius: '4px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <FingerprintCanvas templateBase64={fp.template} imageBase64={fp.image} />
                      <div>
                        <strong>{fp.fingerName}</strong>
                        {fp.template && (
                          <div style={{ fontSize: '11px', color: '#2e7d32', marginTop: '4px' }}>✓ Template stored</div>
                        )}
                        {fp.image && (
                          <div style={{ fontSize: '11px', color: '#1565c0', marginTop: '2px' }}>✓ Image available</div>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  /* Enrolled count > 0 but no records loaded — show placeholder rows */
                  Array.from({ length: formData.biometricEnrolledCount }).map((_, idx) => (
                    <div key={idx} style={{ padding: '8px', background: '#f5f5f5', margin: '4px 0', borderRadius: '4px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{ width: '60px', height: '60px', background: '#e0e0e0', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px' }}>🖐</div>
                      <div>
                        <strong>Finger {idx + 1}</strong>
                        <div style={{ fontSize: '11px', color: '#888', marginTop: '4px' }}>Sync from desktop to load template</div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            <div style={{ marginTop: '2rem' }}>
              <button type="submit" disabled={loading} className="btn primary">
                {loading ? 'Updating...' : (t('save') || 'Update Personnel')}
              </button>
              <button
                type="button"
                onClick={() => onNavigate(`personnel-${subView}`)}
                className="btn secondary"
                style={{ marginLeft: '1rem' }}
              >
                {t('cancel') || 'Cancel'}
              </button>
            </div>
          </form>
        </div>
      </div>

      <WebcamCaptureModal
        isOpen={showWebcamModal}
        onClose={() => setShowWebcamModal(false)}
        onCapture={handleWebcamPhotoCapture}
        title={t('capturePersonnelPhoto', 'Capture Personnel Photo')}
      />
    </>
  );
};

export default EditPersonnel;
